import { EditorProps } from '@monaco-editor/react';
import * as Api from '@ViewModels';
import { editor as MonacoEditor, Position } from 'monaco-editor/esm/vs/editor/editor.api';
import * as React from 'react';
import { useGetAllowedCustomPageStyleClasses } from '../../../../queries';

const OwnerId = 'HostedBlogCssEditor';

function stripPseudoClasses(selector: string) {
	return selector?.replace(/:{1,2}[^{\s}]*/gi, '').trim();
}

function getSelectorFromLastCloseBrace(model: MonacoEditor.IModel, position: Position) {
	const textFromStartToPosition = model.getValueInRange({
		startLineNumber: 1,
		startColumn: 1,
		endLineNumber: position.lineNumber,
		endColumn: position.column,
	});

	const lastCloseBraceIndex = textFromStartToPosition.lastIndexOf('}');
	const startIndex = lastCloseBraceIndex === -1 ? 0 : lastCloseBraceIndex + 1;

	const selectorText = textFromStartToPosition
		.substring(startIndex)
		.replace(/\n|\s+/g, ' ')
		.trim();

	return stripPseudoClasses(selectorText);
}

function getCSSSelectors(content: string) {
	const regex = /(([.#]?[a-z0-9_-]+(?::[:a-z0-9_-]+)*)(?:\s[.#]?[a-z0-9_-]+(?::[:a-z0-9_-]+)*)*)\s*{/gi;
	let match: RegExpExecArray | null;

	const selectors: string[] = [];
	try {
		while ((match = regex.exec(content))) {
			const selector = match[1]?.trim();
			if (selector) {
				selectors.push(selector.toLocaleLowerCase());
			}
		}
		return selectors;
	} catch (error) {
		// Not sure if we want to do something more here
		console.error('Error parsing CSS selectors', error);
		return [];
	}
}

export function useCssEditor({ pageType }: { pageType: Api.CustomPageType }) {
	const [hasError, setHasError] = React.useState<boolean>(false);

	const validClassesRef = React.useRef<string[]>();
	const { data: allowedClasses, isLoading: isLoadingAllowedClasses } = useGetAllowedCustomPageStyleClasses({
		type: pageType,
	});
	validClassesRef.current = allowedClasses;

	const [editorProps] = React.useState<EditorProps>({
		language: 'css',
		options: {
			minimap: { enabled: false },
		},
		onMount: async (editor, monaco) => {
			/**
			 * NOTE: We try to clean this up when the editor instance is disposed, but
			 * if for some reason we need to display multiple instances for the css language,
			 * then they are all going to get this completion item provider.
			 */
			const completionProviderDisposer = monaco.languages.registerCompletionItemProvider('css', {
				triggerCharacters: ['.'],
				provideCompletionItems: (model, position) => {
					if (validClassesRef.current) {
						const validClasses = validClassesRef.current;
						const currentSelector = getSelectorFromLastCloseBrace(model, position);
						const filteredClasses = validClasses.filter(cls => cls.startsWith(currentSelector));

						// NOTE: insert only the remaining part of the matched selector
						const suggestions = filteredClasses.map(cls => ({
							label: cls,
							kind: monaco.languages.CompletionItemKind.Class,
							insertText: cls.replace(currentSelector, ''),
							range: {
								startLineNumber: position.lineNumber,
								startColumn: position.column,
								endLineNumber: position.lineNumber,
								endColumn: position.column,
							},
						}));
						return {
							suggestions,
						};
					}

					return { suggestions: [] };
				},
			});

			// similarly, these are global settings
			monaco.languages.css.cssDefaults.setOptions({
				lint: {
					emptyRules: 'ignore',
				},
			});

			editor.onDidChangeModelDecorations(() => {
				const errorMarkers = (
					monaco.editor.getModelMarkers({
						resource: editor.getModel()?.uri,
					}) || []
				).filter(marker => marker.severity === monaco.MarkerSeverity.Error);
				setHasError(Boolean(errorMarkers.length));
			});

			let timerHandle: NodeJS.Timeout;
			editor.onDidChangeModelContent(() => {
				if (timerHandle) {
					clearTimeout(timerHandle);
				}

				timerHandle = setTimeout(() => {
					const model = editor.getModel();
					if (model) {
						const hasOtherErrorMarkers = monaco.editor
							.getModelMarkers({
								resource: model.uri,
							})
							.some(marker => marker.owner !== OwnerId && marker.severity === monaco.MarkerSeverity.Error);
						if (hasOtherErrorMarkers) {
							// optimization: don't run our validation if there are other errors
							return;
						}

						const markers: ReturnType<typeof monaco.editor.getModelMarkers> = [];

						// Prevent `url()` notation
						// Blunt way of preventing user from creating css keyloger
						// Might need to be more sophisticated in the future, e.g. allow `url()` but only with certain domains (e.g. font loading?)
						model.findMatches('url\\(', true, true, false, null, true)?.forEach(match => {
							markers.push({
								severity: monaco.MarkerSeverity.Error,
								message: 'Use of `url()` notation is not allowed.',
								startLineNumber: match.range.startLineNumber,
								startColumn: match.range.startColumn,
								endLineNumber: match.range.endLineNumber,
								endColumn: match.range.endColumn,
								owner: OwnerId,
								resource: model.uri,
							});
						});

						// optimization: validate if we don't have other errors
						if (validClassesRef.current && markers.length === 0) {
							const value = model.getValue();
							const selectors = getCSSSelectors(value);
							if (selectors.length > 0) {
								const endLineNumber = model.getLineCount();
								const invalidSelectors = selectors.filter(
									selector =>
										!validClassesRef.current.find(x => {
											const normalized = stripPseudoClasses(selector);
											if (x.toLocaleLowerCase() === '.lev') {
												// special case for .lev. if you use startsWith, it will match anything below .lev
												return normalized === x.toLocaleLowerCase();
											}
											return normalized.startsWith(x);
										})
								);
								invalidSelectors.forEach(selector => {
									markers.push({
										severity: monaco.MarkerSeverity.Error,
										message: `Invalid selector: ${selector}`,
										startLineNumber: endLineNumber,
										startColumn: 1,
										endLineNumber,
										endColumn: 1,
										owner: OwnerId,
										resource: model.uri,
									});
								});
							}
						}

						monaco.editor.setModelMarkers(model, OwnerId, markers);
					}
				}, 300);
			});

			editor.onDidDispose(() => {
				completionProviderDisposer?.dispose();
				if (timerHandle) {
					clearTimeout(timerHandle);
					timerHandle = undefined;
				}
			});
		},
	});
	return { editorProps, hasError, isLoadingAllowedClasses, allowedClasses } as const;
}
